import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import GlassCard from '../components/shared/GlassCard';
import Button from '../components/shared/Button';
import Skeleton from '../components/shared/Skeleton';
import RadarChart from '../components/shared/RadarChart';
import {
  MOCK_WORKOUTS,
  MOCK_ATHLETES,
  MOCK_LOGS,
  getLogsForAthlete,
  getWorkoutsForAthlete,
  MOCK_RADAR_SCORES,
} from '../utils/mockData';
import { formatWorkOutput } from '../utils/engineStub';
import './Dashboard.css';

function CoachDashboard({ user }) {
  const recentLogs = MOCK_LOGS.slice(0, 4);
  const upcomingWorkouts = MOCK_WORKOUTS.slice(0, 3);

  return (
    <div className="dashboard">
      <div className="dashboard__header">
        <div>
          <h1 className="dashboard__greeting">
            Good {getTimeOfDay()}, {user.name.split(' ')[0]}
          </h1>
          <p className="dashboard__sub">{MOCK_ATHLETES.length} athletes active today</p>
        </div>
        <Button variant="primary" size="md" as={Link} to="/app/workout/new">
          + New Workout
        </Button>
      </div>

      <GlassCard className="dashboard__radar-card">
        <div className="dashboard__radar-header">
          <span className="dashboard__section-title" style={{ margin: 0 }}>Team Movement Profile</span>
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>Average across all athletes</span>
        </div>
        <RadarChart scores={MOCK_RADAR_SCORES.team} size={260} />
      </GlassCard>

      <div className="dashboard__stat-row">
        <GlassCard size="sm" className="dashboard__stat">
          <span className="dashboard__stat-value">{MOCK_WORKOUTS.length}</span>
          <span className="dashboard__stat-label">Active Programs</span>
        </GlassCard>
        <GlassCard size="sm" className="dashboard__stat">
          <span className="dashboard__stat-value">{MOCK_ATHLETES.length}</span>
          <span className="dashboard__stat-label">Athletes</span>
        </GlassCard>
        <GlassCard size="sm" className="dashboard__stat">
          <span className="dashboard__stat-value">{MOCK_LOGS.length}</span>
          <span className="dashboard__stat-label">Sessions Logged</span>
        </GlassCard>
        <GlassCard size="sm" className="dashboard__stat dashboard__stat--accent">
          <span className="dashboard__stat-value">
            {formatWorkOutput(MOCK_LOGS.reduce((s, l) => s + l.workOutputAchieved, 0))}
          </span>
          <span className="dashboard__stat-label">Total Work Output</span>
        </GlassCard>
      </div>

      <div className="dashboard__grid">
        <section className="dashboard__section">
          <div className="dashboard__section-header">
            <h2 className="dashboard__section-title">Athletes</h2>
            <span className="dashboard__section-count">{MOCK_ATHLETES.length}</span>
          </div>
          <div className="dashboard__athlete-list">
            {MOCK_ATHLETES.map((athlete) => (
              <GlassCard
                key={athlete.id}
                size="sm"
                interactive
                as={Link}
                to={`/app/athlete/${athlete.id}`}
                className="dashboard__athlete-card"
              >
                <div className="dashboard__athlete-avatar">
                  {athlete.name.split(' ').map((n) => n[0]).join('').toUpperCase()}
                </div>
                <div className="dashboard__athlete-info">
                  <span className="dashboard__athlete-name">{athlete.name}</span>
                  <span className="dashboard__athlete-meta">{athlete.position} · {athlete.streak}d streak</span>
                </div>
                <div className={`dashboard__athlete-dot ${getActivityStatus(athlete.lastActive)}`} aria-label="Activity status" />
              </GlassCard>
            ))}
          </div>
        </section>

        <section className="dashboard__section">
          <div className="dashboard__section-header">
            <h2 className="dashboard__section-title">Upcoming Workouts</h2>
            <Link to="/app/schedule" className="dashboard__section-link">View all →</Link>
          </div>
          <div className="dashboard__workout-list">
            {upcomingWorkouts.map((w) => (
              <GlassCard key={w.id} size="sm" interactive className="dashboard__workout-card" as={Link} to={`/app/workout/${w.id}`}>
                <div className="dashboard__workout-info">
                  <span className="dashboard__workout-name">{w.name}</span>
                  <span className="dashboard__workout-meta">
                    {w.scheduledDate} · {w.assignedTo.length} athletes
                  </span>
                </div>
                <span className="dashboard__workout-wu">{formatWorkOutput(w.workOutputTarget)}</span>
              </GlassCard>
            ))}
          </div>
        </section>

        <section className="dashboard__section">
          <div className="dashboard__section-header">
            <h2 className="dashboard__section-title">Recent Logs</h2>
          </div>
          <div className="dashboard__log-list">
            {recentLogs.map((log) => {
              const pct = Math.round((log.workOutputAchieved / log.workOutputTarget) * 100);
              return (
                <GlassCard key={log.id} size="sm" className="dashboard__log-card">
                  <div className="dashboard__log-info">
                    <span className="dashboard__log-wu">{formatWorkOutput(log.workOutputAchieved)}</span>
                    <span className="dashboard__log-date">{log.date}</span>
                  </div>
                  <div className="dashboard__log-bar-track">
                    <div
                      className={`dashboard__log-bar-fill ${pct >= 100 ? 'dashboard__log-bar-fill--exceeded' : ''}`}
                      style={{ width: `${Math.min(pct, 100)}%` }}
                    />
                  </div>
                  <span className="dashboard__log-pct">{pct}% of target</span>
                </GlassCard>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}

function AthleteDashboard({ user }) {
  const navigate = useNavigate();
  const myWorkouts = getWorkoutsForAthlete(user.id);
  const myLogs = getLogsForAthlete(user.id);
  const todayWorkout = myWorkouts[0] || null;
  const totalWU = myLogs.reduce((s, l) => s + l.workOutputAchieved, 0);
  const streak = 7;

  return (
    <div className="dashboard">
      <div className="dashboard__header">
        <div>
          <h1 className="dashboard__greeting">
            Good {getTimeOfDay()}, {user.name.split(' ')[0]}
          </h1>
          <p className="dashboard__sub">
            {todayWorkout ? `Today: ${todayWorkout.name}` : 'Rest day — recover well.'}
          </p>
        </div>
      </div>

      <GlassCard className="dashboard__radar-card">
        <div className="dashboard__radar-header">
          <span className="dashboard__section-title" style={{ margin: 0 }}>My Movement Profile</span>
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>12-axis movement quality</span>
        </div>
        <RadarChart scores={MOCK_RADAR_SCORES['athlete-001']} size={260} />
      </GlassCard>

      <div className="dashboard__stat-row">
        <GlassCard size="sm" className="dashboard__stat dashboard__stat--accent">
          <span className="dashboard__stat-value">{streak}</span>
          <span className="dashboard__stat-label">Day Streak 🔥</span>
        </GlassCard>
        <GlassCard size="sm" className="dashboard__stat">
          <span className="dashboard__stat-value">{myLogs.length}</span>
          <span className="dashboard__stat-label">Sessions Done</span>
        </GlassCard>
        <GlassCard size="sm" className="dashboard__stat">
          <span className="dashboard__stat-value">{formatWorkOutput(totalWU)}</span>
          <span className="dashboard__stat-label">Total Work Output</span>
        </GlassCard>
        <GlassCard size="sm" className="dashboard__stat">
          <span className="dashboard__stat-value">{myWorkouts.length}</span>
          <span className="dashboard__stat-label">Assigned</span>
        </GlassCard>
      </div>

      {todayWorkout && (
        <section className="dashboard__section dashboard__today">
          <h2 className="dashboard__section-title">Today's Session</h2>
          <GlassCard glow className="dashboard__today-card">
            <div className="dashboard__today-inner">
              <div>
                <p className="dashboard__today-name">{todayWorkout.name}</p>
                <p className="dashboard__today-meta">
                  {todayWorkout.exercises.length} exercises · {todayWorkout.duration} min
                </p>
              </div>
              <div className="dashboard__today-wu">
                <span className="dashboard__today-wu-value">{formatWorkOutput(todayWorkout.workOutputTarget)}</span>
                <span className="dashboard__today-wu-label">target</span>
              </div>
            </div>
            <Button variant="primary" size="lg" className="dashboard__today-start" onClick={() => navigate(`/app/workouts/${todayWorkout.id}`)}>
              Start Session
            </Button>
          </GlassCard>
        </section>
      )}

      <section className="dashboard__section">
        <div className="dashboard__section-header">
          <h2 className="dashboard__section-title">Assigned Workouts</h2>
          <Link to="/app/schedule" className="dashboard__section-link">Schedule →</Link>
        </div>
        <div className="dashboard__workout-list">
          {myWorkouts.map((w) => (
            <GlassCard key={w.id} size="sm" interactive className="dashboard__workout-card" as={Link} to={`/app/workout/${w.id}`}>
              <div className="dashboard__workout-info">
                <span className="dashboard__workout-name">{w.name}</span>
                <span className="dashboard__workout-meta">{w.scheduledDate} · {w.duration} min</span>
              </div>
              <span className="dashboard__workout-wu">{formatWorkOutput(w.workOutputTarget)}</span>
            </GlassCard>
          ))}
        </div>
      </section>

      <section className="dashboard__section">
        <div className="dashboard__section-header">
          <h2 className="dashboard__section-title">Recent Activity</h2>
        </div>
        <div className="dashboard__log-list">
          {myLogs.map((log) => {
            const pct = Math.round((log.workOutputAchieved / log.workOutputTarget) * 100);
            return (
              <GlassCard key={log.id} size="sm" className="dashboard__log-card">
                <div className="dashboard__log-info">
                  <span className="dashboard__log-wu">{formatWorkOutput(log.workOutputAchieved)}</span>
                  <span className="dashboard__log-date">{log.date}</span>
                </div>
                <div className="dashboard__log-bar-track">
                  <div
                    className={`dashboard__log-bar-fill ${pct >= 100 ? 'dashboard__log-bar-fill--exceeded' : ''}`}
                    style={{ width: `${Math.min(pct, 100)}%` }}
                  />
                </div>
                <span className="dashboard__log-pct">{pct}% of target</span>
              </GlassCard>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function getTimeOfDay() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

function getActivityStatus(dateStr) {
  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  if (dateStr === today) return 'dashboard__athlete-dot--active';
  if (dateStr === yesterday) return 'dashboard__athlete-dot--recent';
  return 'dashboard__athlete-dot--inactive';
}

export default function Dashboard() {
  const { user, isCoach } = useAuth();
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 550);
    return () => clearTimeout(t);
  }, []);

  if (loading) {
    return (
      <div className="dashboard">
        <div className="dashboard__header">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Skeleton width={200} height={28} />
            <Skeleton width={160} height={16} />
          </div>
        </div>
        <GlassCard style={{ padding: 'var(--space-5)' }}>
          <Skeleton width="100%" height={280} />
        </GlassCard>
        <div className="dashboard__stat-row">
          {[1,2,3,4].map((i) => <GlassCard key={i} size="sm" className="dashboard__stat"><Skeleton width="100%" height={50} /></GlassCard>)}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
          {[1,2,3].map((i) => <Skeleton key={i} width="100%" height={72} />)}
        </div>
      </div>
    );
  }

  return isCoach ? <CoachDashboard user={user} /> : <AthleteDashboard user={user} />;
}
